import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { productsApi } from '../../services/api';
import { Input } from '../../components/ui/input';
import ImageUpload from '../../components/common/ImageUpload';
import { toast } from 'sonner';

const AdminAddProduct = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    productName: '',
    productType: '',
    productPrice: '',
    productCategory: '',
    mainImg: '',
    images: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleMainImageChange = (imageData) => {
    setFormData((prev) => ({ ...prev, mainImg: imageData }));
  };

  const handleGalleryChange = (imagesData) => {
    setFormData((prev) => ({ ...prev, images: imagesData }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.productName || !formData.productPrice) {
      toast.error('Ad və qiymət sahələri mütləqdir');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        productName: formData.productName,
        productType: formData.productType,
        productPrice: parseFloat(formData.productPrice) || 0,
        productCategory: formData.productCategory,
        mainImg: formData.mainImg,
        images: formData.images.map((img) => ({ img })),
      };

      await productsApi.create(payload);
      toast.success('Məhsul uğurla əlavə edildi!');
      navigate('/admin/products');
    } catch (err) {
      console.error('Error adding product:', err);
      toast.error('Məhsul əlavə etmək mümkün olmadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-add-product">
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/products')}
          className="inline-flex items-center gap-2 text-brand-muted hover:text-brand-primary transition-colors mb-4"
          data-testid="back-to-products"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-medium">Məhsullara qayıt</span>
        </button>

        <h1 className="font-serif text-2xl text-brand-brown">Yeni məhsul əlavə et</h1>
        <p className="text-sm text-brand-muted mt-1">Məhsul məlumatlarını daxil edin</p>
      </div>

      <div className="bg-white rounded-2xl p-8 shadow-card">
        <form onSubmit={handleSubmit} className="space-y-8" data-testid="add-product-form">
          <div>
            <h3 className="font-serif text-lg text-brand-brown mb-6 pb-3 border-b border-brand-secondary/30">
              Əsas məlumatlar
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-brand-brown mb-2">
                  Məhsul adı <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="productName"
                  value={formData.productName}
                  onChange={handleChange}
                  required
                  placeholder="Məs: Toxuma kukla"
                  className="rounded-xl border-brand-secondary focus:border-brand-primary"
                  data-testid="input-productName"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-2">
                  Model / Növ
                </label>
                <Input
                  type="text"
                  name="productType"
                  value={formData.productType}
                  onChange={handleChange}
                  placeholder="Məs: Əl işi dekor"
                  className="rounded-xl border-brand-secondary focus:border-brand-primary"
                  data-testid="input-productType"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-2">
                  Qiymət (₼) <span className="text-red-500">*</span>
                </label>
                <Input
                  type="number"
                  name="productPrice"
                  value={formData.productPrice}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  className="rounded-xl border-brand-secondary focus:border-brand-primary"
                  data-testid="input-productPrice"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-brand-brown mb-2">
                  Kateqoriya
                </label>
                <Input
                  type="text"
                  name="productCategory"
                  value={formData.productCategory}
                  onChange={handleChange}
                  placeholder="Məs: Kukla"
                  className="rounded-xl border-brand-secondary focus:border-brand-primary"
                  data-testid="input-productCategory"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="font-serif text-lg text-brand-brown mb-6 pb-3 border-b border-brand-secondary/30">
              Şəkillər
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <ImageUpload
                value={formData.mainImg}
                onChange={handleMainImageChange}
                label="Əsas şəkil"
                uploadType="product"
                className="flex-1"
              />

              <ImageUpload
                value={formData.images}
                onChange={handleGalleryChange}
                multiple={true}
                label="Qalereya şəkilləri (bir neçə seçə bilərsiniz)"
                uploadType="product"
                className="flex-1"
              />
            </div>
          </div>

          <div className="flex items-center gap-4 pt-6 border-t border-brand-secondary/30">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-primary to-[#E08E73] text-white px-8 py-3 font-medium hover:shadow-warm transition-all duration-300 disabled:opacity-50"
              data-testid="submit-product"
            >
              {loading ? (
                'Əlavə edilir...'
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  Yadda saxla
                </>
              )}
            </button>

            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="px-8 py-3 text-brand-muted hover:text-brand-primary transition-colors"
              data-testid="cancel-add-product"
            >
              Ləğv et
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminAddProduct;